import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Typography, Table, Alert, Button, Progress, Space } from 'antd';
import {
  GlobalOutlined, UserOutlined, DatabaseOutlined, CloudServerOutlined,
  CheckCircleOutlined, WarningOutlined, InfoCircleOutlined, RightOutlined, SwapOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dnsService from '../services/dnsService';
import userService from '../services/userService';
import systemService from '../services/systemService';

import { adminPath } from '../../shared/config/paths';

const { Title } = Typography;

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    zones: 0,
    records: 0,
    users: 0,
    nodes: 0,
    activeZones: 0,
    activeCidrs: 0,
    systemHealth: {}
  });
  const [recentZones, setRecentZones] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [nodeStatus, setNodeStatus] = useState([]);

  // 获取仪表板数据
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // 获取系统统计信息
        const statsResult = await systemService.getSystemStats();
        if (statsResult.success) {
          setStats(statsResult.data);
        }

        // 获取最近更新的区域
        const zonesResult = await dnsService.getDnsZones({ page: 1, page_size: 5, sortField: 'updated_at', sortOrder: 'desc' });
        if (zonesResult.success) {
          setRecentZones(zonesResult.data.slice(0, 5));
        }

        // 获取最近注册的用户
        const usersResult = await userService.getUsers({ limit: 5, sort: 'created_at:desc' });
        if (usersResult.success) {
          setRecentUsers(usersResult.data.slice(0, 5));
        }

        // 获取节点状态
        const nodesResult = await dnsService.getDnsNodes();
        if (nodesResult.success) {
          setNodeStatus(nodesResult.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const zoneColumns = [
    {
      title: '区域',
      dataIndex: 'zone',
      key: 'zone',
      render: (text, record) => <Link to={adminPath(`/dns/records/${record.id}`)}>{text}</Link>,
    },
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '记录数',
      dataIndex: 'record_count',
      key: 'record_count',
      render: count => count || 0
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        status === 'active' ?
          <span style={{ color: '#52c41a' }}><CheckCircleOutlined /> 活跃</span> :
          <span style={{ color: '#faad14' }}><WarningOutlined /> 未激活</span>
      )
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <Link to={adminPath(`/dns/records/${record.id}`)}>
          <Button type="link" icon={<RightOutlined />} size="small">管理</Button>
        </Link>
      )
    }
  ];

  const userColumns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      render: (text, record) => <Link to={adminPath(`/users/detail/${record.id}`)}>{text}</Link>,
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: role => role === 'admin' ? <span style={{color: '#722ed1'}}>管理员</span> : '用户'
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: text => new Date(text).toLocaleString()
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <Link to={adminPath(`/users/detail/${record.id}`)}>
          <Button type="link" icon={<RightOutlined />} size="small">查看</Button>
        </Link>
      )
    }
  ];

  const nodeColumns = [
    {
      title: '节点',
      dataIndex: 'hostname',
      key: 'hostname',
      render: (text, record) => <Link to={adminPath("/dns/nodes")}>{text}</Link>,
    },
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: status => {
        switch(status) {
          case 'active':
            return <span style={{ color: '#52c41a' }}><CheckCircleOutlined /> 活跃</span>;
          case 'inactive':
            return <span style={{ color: '#faad14' }}><WarningOutlined /> 未激活</span>;
          case 'maintenance':
            return <span style={{ color: '#1890ff' }}><InfoCircleOutlined /> 维护中</span>;
          default:
            return <span style={{ color: '#ff4d4f' }}><WarningOutlined /> 未知</span>;
        }
      }
    },
    {
      title: '同步状态',
      dataIndex: 'sync_status',
      key: 'sync_status',
      render: (_, record) => {
        if (!record.last_sync) return <span>未同步</span>;

        const lastSync = new Date(record.last_sync);
        const now = new Date();
        const hoursDiff = (now - lastSync) / (1000 * 60 * 60);

        if (hoursDiff < 1) return <span style={{ color: '#52c41a' }}>正常</span>;
        if (hoursDiff < 24) return <span style={{ color: '#faad14' }}>需要同步</span>;

        return <span style={{ color: '#ff4d4f' }}>同步超时</span>;
      }
    }
  ];

  const handleSwitchToUserPanel = () => {
    // 切换到用户前端
    window.location.href = '/';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>系统仪表板</Title>
        <Button
          type="primary"
          icon={<SwapOutlined />}
          onClick={handleSwitchToUserPanel}
        >
          切换到用户界面
        </Button>
      </div>

      <Row gutter={16}>
        <Col xs={24} sm={12} md={6}>
          <Card className="dashboard-card" loading={loading}>
            <Statistic
              title="DNS区域"
              value={stats.zones}
              prefix={<GlobalOutlined />}
              suffix={<Link to={adminPath("/dns/zones")}>管理</Link>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="dashboard-card" loading={loading}>
            <Statistic
              title="用户"
              value={stats.users}
              prefix={<UserOutlined />}
              suffix={<Link to={adminPath("/users")}>管理</Link>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="dashboard-card" loading={loading}>
            <Statistic
              title="DNS记录"
              value={stats.records}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="dashboard-card" loading={loading}>
            <Statistic
              title="DNS节点"
              value={stats.nodes}
              prefix={<CloudServerOutlined />}
              suffix={<Link to={adminPath("/dns/nodes")}>管理</Link>}
            />
          </Card>
        </Col>
      </Row>

      {/* 系统状态 */}
      <Card title="系统状态" className="dashboard-card" loading={loading}>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Card type="inner" title="CPU使用率">
              <Progress
                percent={parseFloat((stats.systemHealth?.cpu_usage || 0).toFixed(1))}
                status={stats.systemHealth?.cpu_usage > 80 ? "exception" : "normal"}
                format={percent => `${percent}%`}
              />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card type="inner" title="内存使用率">
              <Progress
                percent={parseFloat((stats.systemHealth?.memory_usage || 0).toFixed(1))}
                status={stats.systemHealth?.memory_usage > 80 ? "exception" : "normal"}
                format={percent => `${percent}%`}
              />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card type="inner" title="磁盘使用率">
              <Progress
                percent={parseFloat((stats.systemHealth?.disk_usage || 0).toFixed(1))}
                status={stats.systemHealth?.disk_usage > 80 ? "exception" : "normal"}
                format={percent => `${percent}%`}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card
            title="最近更新的区域"
            extra={<Link to={adminPath("/dns/zones")}>查看全部</Link>}
            className="dashboard-card"
            loading={loading}
          >
            {recentZones.length > 0 ? (
              <Table
                columns={zoneColumns}
                dataSource={recentZones}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ) : (
              <Alert
                message="暂无区域"
                description="系统中尚未创建任何DNS区域。"
                type="info"
                showIcon
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title="最近注册的用户"
            extra={<Link to={adminPath("/users")}>查看全部</Link>}
            className="dashboard-card"
            loading={loading}
          >
            {recentUsers.length > 0 ? (
              <Table
                columns={userColumns}
                dataSource={recentUsers}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ) : (
              <Alert
                message="暂无用户"
                description="系统中尚未注册任何用户。"
                type="info"
                showIcon
              />
            )}
          </Card>
        </Col>
      </Row>

      <Card
        title="DNS节点状态"
        extra={<Link to={adminPath("/dns/nodes")}>管理节点</Link>}
        className="dashboard-card"
        loading={loading}
      >
        {nodeStatus.length > 0 ? (
          <Table
            columns={nodeColumns}
            dataSource={nodeStatus}
            rowKey="id"
            pagination={false}
            size="small"
          />
        ) : (
          <Alert
            message="暂无节点"
            description="系统中尚未配置任何DNS节点。"
            type="info"
            showIcon
            action={
              <Link to={adminPath("/dns/nodes")}>
                <Button size="small" type="primary">
                  添加节点
                </Button>
              </Link>
            }
          />
        )}
      </Card>
    </div>
  );
};

export default Dashboard;
